import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiShield,
    FiX,
} from "react-icons/fi";

import RolePermissionMatrix
    from "./RolePermissionMatrix";


/*
|--------------------------------------------------------------------------
| INITIAL FORM
|--------------------------------------------------------------------------
*/

const INITIAL_FORM = {

    name:
        "",

    code:
        "",

    description:
        "",

    status:
        "ACTIVE",

    isDefault:
        false,

    dataScope:
        "SELF",

    permissions:
        [],

};


/*
|--------------------------------------------------------------------------
| NORMALIZE ROLE CODE
|--------------------------------------------------------------------------
*/

const normalizeRoleCode =
    (
        value
    ) => {

        return String(
            value ||
            ""
        )
            .trim()
            .toUpperCase()
            .replace(
                /[^A-Z0-9]+/g,
                "_"
            )
            .replace(
                /^_+|_+$/g,
                ""
            );

    };


/*
|--------------------------------------------------------------------------
| OBJECT ID
|--------------------------------------------------------------------------
*/

const getObjectId =
    (
        value
    ) => {

        if (
            !value
        ) {

            return "";

        }


        if (
            typeof value ===
            "string"
        ) {

            return value;

        }


        return String(
            value?._id ||
            value?.id ||
            ""
        );

    };


/*
|--------------------------------------------------------------------------
| ROLE FORM MODAL
|--------------------------------------------------------------------------
*/

const RoleFormModal =
    ({
        open = false,

        mode = "CREATE",

        role = null,

        permissionModules = [],

        loading = false,

        onClose,

        onSubmit,
    }) => {

        /*
        |--------------------------------------------------------------------------
        | FORM
        |--------------------------------------------------------------------------
        */

        const [
            form,
            setForm,
        ] =
            useState({
                ...INITIAL_FORM,
            });


        /*
        |--------------------------------------------------------------------------
        | ERRORS
        |--------------------------------------------------------------------------
        */

        const [
            errors,
            setErrors,
        ] =
            useState({});


        /*
        |--------------------------------------------------------------------------
        | EDIT MODE
        |--------------------------------------------------------------------------
        */

        const isEdit =
            String(
                mode ||
                ""
            )
                .trim()
                .toUpperCase() ===
            "EDIT";


        /*
        |--------------------------------------------------------------------------
        | SYSTEM ROLE
        |--------------------------------------------------------------------------
        */

        const isSystemRole =
            Boolean(
                role
                    ?.isSystem
            );


        const isProtectedRole =
            Boolean(
                role
                    ?.isProtected
            );


        /*
        |--------------------------------------------------------------------------
        | LOAD FORM
        |--------------------------------------------------------------------------
        */

        useEffect(
            () => {

                if (
                    !open
                ) {

                    return;

                }


                if (
                    isEdit &&
                    role
                ) {

                    const permissionIds =
                        Array.isArray(
                            role
                                ?.permissions
                        )
                            ?
                            role
                                .permissions
                                .map(
                                    getObjectId
                                )
                                .filter(
                                    Boolean
                                )
                            :
                            [];


                    setForm({

                        name:
                            role
                                ?.name ||
                            "",

                        code:
                            role
                                ?.code ||
                            "",

                        description:
                            role
                                ?.description ||
                            "",

                        status:
                            role
                                ?.status ||
                            "ACTIVE",

                        isDefault:
                            Boolean(
                                role
                                    ?.isDefault
                            ),

                        dataScope:
                            String(
                                role
                                    ?.dataScope ||
                                (
                                    String(
                                        role
                                            ?.code ||
                                        ""
                                    )
                                        .trim()
                                        .toUpperCase() ===
                                    "OWNER"
                                        ?
                                        "ALL"
                                        :
                                        "SELF"
                                )
                            )
                                .trim()
                                .toUpperCase(),

                        permissions:
                            permissionIds,

                    });

                } else {

                    setForm({
                        ...INITIAL_FORM,
                    });

                }


                setErrors(
                    {}
                );

            },
            [
                open,
                isEdit,
                role,
            ]
        );


        /*
        |--------------------------------------------------------------------------
        | SELECTED COUNT
        |--------------------------------------------------------------------------
        */

        const selectedPermissionCount =
            useMemo(
                () => {

                    return Array.isArray(
                        form.permissions
                    )
                        ?
                        form
                            .permissions
                            .length
                        :
                        0;

                },
                [
                    form.permissions,
                ]
            );


        /*
        |--------------------------------------------------------------------------
        | UPDATE FORM
        |--------------------------------------------------------------------------
        */

        const updateForm =
            (
                field,
                value
            ) => {

                setForm(
                    (
                        previous
                    ) => ({

                        ...previous,

                        [field]:
                            value,

                    })
                );


                setErrors(
                    (
                        previous
                    ) => ({

                        ...previous,

                        [field]:
                            "",

                        form:
                            "",

                    })
                );

            };


        /*
        |--------------------------------------------------------------------------
        | NAME CHANGE
        |--------------------------------------------------------------------------
        */

        const handleNameChange =
            (
                event
            ) => {

                const value =
                    event
                        .target
                        .value;


                setForm(
                    (
                        previous
                    ) => {

                        const shouldGenerateCode =
                            !isEdit &&
                            (
                                !previous.code ||
                                previous.code ===
                                    normalizeRoleCode(
                                        previous.name
                                    )
                            );


                        return {

                            ...previous,

                            name:
                                value,

                            code:
                                shouldGenerateCode
                                    ?
                                    normalizeRoleCode(
                                        value
                                    )
                                    :
                                    previous.code,

                        };

                    }
                );


                setErrors(
                    (
                        previous
                    ) => ({

                        ...previous,

                        name:
                            "",

                        code:
                            "",

                        form:
                            "",

                    })
                );

            };


        /*
        |--------------------------------------------------------------------------
        | VALIDATE
        |--------------------------------------------------------------------------
        */

        const validate =
            () => {

                const nextErrors =
                    {};


                if (
                    !String(
                        form.name ||
                        ""
                    )
                        .trim()
                ) {

                    nextErrors.name =
                        "Role name is required.";

                }


                if (
                    !String(
                        form.code ||
                        ""
                    )
                        .trim()
                ) {

                    nextErrors.code =
                        "Role code is required.";

                }


                if (
                    ![
                        "ACTIVE",
                        "INACTIVE",
                    ].includes(
                        String(
                            form.status ||
                            ""
                        )
                            .trim()
                            .toUpperCase()
                    )
                ) {

                    nextErrors.status =
                        "Valid role status is required.";

                }


                if (
                    !Array.isArray(
                        form.permissions
                    )
                ) {

                    nextErrors.permissions =
                        "Permissions must be valid.";

                }


                setErrors(
                    nextErrors
                );


                return (
                    Object.keys(
                        nextErrors
                    )
                        .length ===
                    0
                );

            };


        /*
        |--------------------------------------------------------------------------
        | SUBMIT
        |--------------------------------------------------------------------------
        */

        const handleSubmit =
            async (
                event
            ) => {

                event
                    .preventDefault();


                if (
                    loading
                ) {

                    return;

                }


                if (
                    !validate()
                ) {

                    return;

                }


                const payload = {

                    name:
                        String(
                            form.name ||
                            ""
                        )
                            .trim(),

                    code:
                        normalizeRoleCode(
                            form.code
                        ),

                    description:
                        String(
                            form.description ||
                            ""
                        )
                            .trim(),

                    status:
                        String(
                            form.status ||
                            "ACTIVE"
                        )
                            .trim()
                            .toUpperCase(),

                    isDefault:
                        Boolean(
                            form.isDefault
                        ),

                    dataScope:
                        String(
                            form.dataScope ||
                            "SELF"
                        )
                            .trim()
                            .toUpperCase(),

                    permissions:
                        [
                            ...new Set(
                                (
                                    form.permissions ||
                                    []
                                )
                                    .map(
                                        String
                                    )
                                    .filter(
                                        Boolean
                                    )
                            ),
                        ],

                };


                try {

                    if (
                        typeof onSubmit ===
                        "function"
                    ) {

                        await onSubmit(
                            payload
                        );

                    }

                } catch (
                    error
                ) {

                    setErrors(
                        (
                            previous
                        ) => ({

                            ...previous,

                            form:
                                error
                                    ?.message ||
                                "Unable to save role.",

                        })
                    );

                }

            };


        /*
        |--------------------------------------------------------------------------
        | CLOSE
        |--------------------------------------------------------------------------
        */

        const handleClose =
            () => {

                if (
                    loading
                ) {

                    return;

                }


                if (
                    typeof onClose ===
                    "function"
                ) {

                    onClose();

                }

            };


        /*
        |--------------------------------------------------------------------------
        | CLOSED
        |--------------------------------------------------------------------------
        */

        if (
            !open
        ) {

            return null;

        }


        /*
        |--------------------------------------------------------------------------
        | UI
        |--------------------------------------------------------------------------
        */

        return (

            <div className="role-modal-backdrop">

                <div className="role-modal">

                    {/* ============================================================
                        HEADER
                    ============================================================ */}

                    <div className="role-modal-header">

                        <div>

                            <span className="role-modal-icon">
                                <FiShield />
                            </span>


                            <div>

                                <h2>

                                    {
                                        isEdit
                                            ?
                                            "Edit Role"
                                            :
                                            "Create Role"
                                    }

                                </h2>


                                <p>

                                    {
                                        isEdit
                                            ?
                                            "Update role information and permission access."
                                            :
                                            "Create a tenant role and assign permissions from subscribed modules."
                                    }

                                </p>

                            </div>

                        </div>


                        <button
                            type="button"

                            className="role-modal-close"

                            onClick={
                                handleClose
                            }

                            disabled={
                                loading
                            }
                        >
                            <FiX />
                        </button>

                    </div>


                    {/* ============================================================
                        SYSTEM ROLE WARNING
                    ============================================================ */}

                    {
                        isSystemRole
                            ?
                            (
                                <div className="role-system-notice">

                                    <FiShield />


                                    <div>

                                        <strong>
                                            System Role
                                        </strong>


                                        <span>

                                            {
                                                isProtectedRole
                                                    ?
                                                    "This is a protected system role. Some fields may not be editable."
                                                    :
                                                    "This role was created by the system."
                                            }

                                        </span>

                                    </div>

                                </div>
                            )
                            :
                            null
                    }


                    {/* ============================================================
                        FORM
                    ============================================================ */}

                    <form
                        onSubmit={
                            handleSubmit
                        }
                    >

                        <div className="role-modal-body">

                            {/* ====================================================
                                BASIC INFORMATION
                            ==================================================== */}

                            <section className="role-form-section">

                                <div className="role-form-section-heading">

                                    <strong>
                                        Role Information
                                    </strong>


                                    <span>
                                        Basic identity and role behavior.
                                    </span>

                                </div>


                                <div className="role-form-grid">

                                    {/* ============================================
                                        NAME
                                    ============================================ */}

                                    <div className="role-form-field">

                                        <label>
                                            Role Name *
                                        </label>


                                        <input
                                            type="text"

                                            value={
                                                form.name
                                            }

                                            onChange={
                                                handleNameChange
                                            }

                                            placeholder="Example: Store Manager"

                                            disabled={
                                                loading ||
                                                isProtectedRole
                                            }
                                        />


                                        {
                                            errors?.name
                                                ?
                                                (
                                                    <small className="role-form-error">
                                                        {
                                                            errors.name
                                                        }
                                                    </small>
                                                )
                                                :
                                                null
                                        }

                                    </div>


                                    {/* ============================================
                                        CODE
                                    ============================================ */}

                                    <div className="role-form-field">

                                        <label>
                                            Role Code *
                                        </label>


                                        <input
                                            type="text"

                                            value={
                                                form.code
                                            }

                                            onChange={
                                                (
                                                    event
                                                ) =>
                                                    updateForm(
                                                        "code",
                                                        normalizeRoleCode(
                                                            event
                                                                .target
                                                                .value
                                                        )
                                                    )
                                            }

                                            placeholder="STORE_MANAGER"

                                            disabled={
                                                loading ||
                                                isSystemRole
                                            }
                                        />


                                        <small>
                                            Internal unique code for this tenant.
                                        </small>


                                        {
                                            errors?.code
                                                ?
                                                (
                                                    <small className="role-form-error">
                                                        {
                                                            errors.code
                                                        }
                                                    </small>
                                                )
                                                :
                                                null
                                        }

                                    </div>


                                    {/* ============================================
                                        STATUS
                                    ============================================ */}

                                    <div className="role-form-field">

                                        <label>
                                            Status
                                        </label>


                                        <select
                                            value={
                                                form.status
                                            }

                                            onChange={
                                                (
                                                    event
                                                ) =>
                                                    updateForm(
                                                        "status",
                                                        event
                                                            .target
                                                            .value
                                                    )
                                            }

                                            disabled={
                                                loading ||
                                                isProtectedRole
                                            }
                                        >

                                            <option value="ACTIVE">
                                                Active
                                            </option>

                                            <option value="INACTIVE">
                                                Inactive
                                            </option>

                                        </select>

                                    </div>


                                    {/* ============================================
                                        EMPLOYEE DATA SCOPE
                                    ============================================ */}

                                    <div className="role-form-field">

                                        <label>
                                            Employee Data Access
                                        </label>


                                        <select
                                            value={
                                                form.dataScope ||
                                                "SELF"
                                            }

                                            onChange={
                                                (
                                                    event
                                                ) =>
                                                    updateForm(
                                                        "dataScope",
                                                        event
                                                            .target
                                                            .value
                                                    )
                                            }

                                            disabled={
                                                loading ||
                                                isProtectedRole
                                            }
                                        >

                                            <option value="SELF">
                                                Own Data Only (SELF)
                                            </option>

                                            <option value="TEAM">
                                                Own + Direct Team (TEAM)
                                            </option>

                                            <option value="DEPARTMENT">
                                                Same Department (DEPARTMENT)
                                            </option>

                                            <option value="ALL">
                                                All Employees (ALL)
                                            </option>

                                        </select>


                                        <small>
                                            Controls which employee records this role may access. It does not change module/action permissions.
                                        </small>

                                    </div>


                                    {/* ============================================
                                        DEFAULT
                                    ============================================ */}

                                    <div className="role-form-field">

                                        <label>
                                            Default Employee Role
                                        </label>


                                        <label className="role-default-toggle">

                                            <input
                                                type="checkbox"

                                                checked={
                                                    Boolean(
                                                        form.isDefault
                                                    )
                                                }

                                                onChange={
                                                    (
                                                        event
                                                    ) =>
                                                        updateForm(
                                                            "isDefault",
                                                            event
                                                                .target
                                                                .checked
                                                        )
                                                }

                                                disabled={
                                                    loading ||
                                                    isProtectedRole
                                                }
                                            />


                                            <span />


                                            <div>

                                                <strong>
                                                    Use as default
                                                </strong>


                                                <small>
                                                    Preselect this role when creating employees.
                                                </small>

                                            </div>

                                        </label>

                                    </div>


                                    {/* ============================================
                                        DESCRIPTION
                                    ============================================ */}

                                    <div className="role-form-field role-form-field-full">

                                        <label>
                                            Description
                                        </label>


                                        <textarea
                                            rows="3"

                                            value={
                                                form.description
                                            }

                                            onChange={
                                                (
                                                    event
                                                ) =>
                                                    updateForm(
                                                        "description",
                                                        event
                                                            .target
                                                            .value
                                                    )
                                            }

                                            placeholder="Describe what users with this role are responsible for..."

                                            disabled={
                                                loading
                                            }
                                        />

                                    </div>

                                </div>

                            </section>


                            {/* ====================================================
                                PERMISSIONS
                            ==================================================== */}

                            <section className="role-form-section role-form-permission-section">

                                <RolePermissionMatrix

                                    permissionModules={
                                        permissionModules
                                    }

                                    value={
                                        form.permissions
                                    }

                                    onChange={
                                        (
                                            permissionIds
                                        ) =>
                                            updateForm(
                                                "permissions",
                                                permissionIds
                                            )
                                    }

                                    disabled={
                                        loading ||
                                        isProtectedRole
                                    }

                                    error={
                                        errors?.permissions ||
                                        ""
                                    }

                                />

                            </section>


                            {/* ====================================================
                                FORM ERROR
                            ==================================================== */}

                            {
                                errors?.form
                                    ?
                                    (
                                        <div className="role-form-api-error">

                                            {
                                                errors.form
                                            }

                                        </div>
                                    )
                                    :
                                    null
                            }

                        </div>


                        {/* ========================================================
                            FOOTER
                        ======================================================== */}

                        <div className="role-modal-footer">

                            <button
                                type="button"

                                className="secondary"

                                onClick={
                                    handleClose
                                }

                                disabled={
                                    loading
                                }
                            >
                                Cancel
                            </button>


                            <button
                                type="submit"

                                className="primary"

                                disabled={
                                    loading ||
                                    isProtectedRole
                                }
                            >

                                {
                                    loading
                                        ?
                                        "Saving..."
                                        :
                                        isEdit
                                            ?
                                            "Update Role"
                                            :
                                            "Create Role"
                                }

                            </button>

                        </div>

                    </form>

                </div>

            </div>

        );

    };


export default
    RoleFormModal;